/*
 * La Lepre - Ceramics Studio Portfolio Website
 * Developed by LEVERAWEB | Franco Levera
 * Website: leveraweb.com
 *
 * This website showcases the artistry and craftsmanship of La Lepre ceramics studio
 * Built with React, Tailwind CSS, and DirectUS CMS
 */

import { useState } from "react";
import Header from "./components/Header";
import Hero from "./components/Hero";
import Courses from "./components/Courses";
import Creators from "./components/Creators";
import WorkWithMe from "./components/WorkWithMe";
import OurHome from "./components/OurHome";
import About from "./components/About";
import Gallery from "./components/Gallery";
import Footer from "./components/Footer";
import TopBar from "./components/TopBar";
import Button from './components/Button';
import { GlobalProvider, useGlobal } from "./contexts/GlobalContext";

function AppContent() {
  const { t, i18n, iUrl, parse, topbarStatus, isLoading, directusGlobals } = useGlobal();

  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen font-display text-secondary-300"></div>;
  }

  return (
    <div className="app min-h-screen bg-gray-50">

      <TopBar
        isVisible={topbarStatus.status}
        content={t('topbar.0.notice_text')}
        bgColor={topbarStatus.color || 'bg-secondary-700'}
      />
      <Header />
      <Hero />
      {/* First Two-Column Section - Ceramics Studio */}
      <section className="first-section md:py-16">
        <div className="first-section__grid grid grid-cols-1 lg:grid-cols-2 gap-0 lg:gap-12 items-center">
          {/* Left - Image */}
          <div className="first-section__inner tw-full">
            <img
              src={`${iUrl}${t("sections_data.0.section_cover")}?format=webp`}
              alt="Ceramics studio workspace"
              className="first-section__img w-full h-[500px] object-cover  shadow-lg"
            />
          </div>

          {/* Right - Text Content */}
          <div className="first-section__content space-y-6 px-8 py-12 lg:px-0 max-w-content mx-auto w-full lg:max-w-none">
            <h2 className="first-section__title font-medium font-header text-4xl lg:text-5xl text-secondary-700">
              {t("sections_data.0.section_heading")}
            </h2>
            <div className="first-section__paragraph font-body text-lg text-dark-700 leading-relaxed">
              {parse(t("sections_data.0.section_body"))}
            </div>
            <Button href="#courses" size="medium">
              {t("sections_data.0.section_cta")}
            </Button>
          </div>
        </div>
      </section>

      {/* Dynamic Alternating Sections */}
      <section className="second-section py-16">
        {[1, 2, 3, 8].map((index) => {
          const isEven = index % 2 === 0;
          const colors = ['bg-bg-alt-500', 'bg-primary-300', 'bg-secondary-300'];
          const bgColor = colors[index - 1];

          return (
            <div
              key={index}
              className={`second-section__row grid grid-cols-1 lg:grid-cols-2 min-h-[60vh]`}
            >
              <div className={`second-section__image-wrapper w-full ${isEven ? 'order-1 lg:order-2' : ''}`}>
                <img
                  src={`${iUrl}${t(`sections_data.${index}.section_cover`)}?format=webp`}
                  alt={t(`sections_data.${index}.section_heading`)}
                  className="second-section__image w-full h-full object-cover"
                />
              </div>
              <div className={`second-section__content flex items-center ${bgColor} px-8 lg:px-16 py-12 ${isEven ? 'order-2 lg:order-1' : ''}`}>
                <div className="second-section__inner space-y-4">
                  <h3 className="font-medium second-section__title font-header text-3xl text-dark-900">
                    {t(`sections_data.${index}.section_heading`)}
                  </h3>
                  <div className="second-section__paragraph font-body text-lg text-dark-700 leading-relaxed">
                    {parse(t(`sections_data.${index}.section_body`))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </section>

      <Courses />
      <Creators />
      <WorkWithMe />
      <OurHome />
      <About />
      <Gallery />

      <main className="app__main container mx-auto px-4 py-8"></main>

      <Footer />
    </div>
  );
}

function App() {
  return (
    <GlobalProvider>
      <AppContent />
    </GlobalProvider>
  );
}

export default App;
